import {
  Box,
  FormControl,
  FormGroup,
  FormGroupProps,
  Typography,
} from "@mui/material";
import { ChangeEvent, Fragment, ReactElement } from "react";
import { CarCheckbox } from "./Checkbox";
import { CarIconCheck, CarIconRemove } from "@app/components/Icons";

export interface CarTreeItem {
  id: string;
  label: string;
  parentId?: string;
  children: CarTreeItem[];
}

export interface CarTreeSelectProps extends Omit<FormGroupProps, "onChange"> {
  items: Array<CarTreeItem>;
  checkedItems: Array<string>;
  onChange: (checkedItems: Array<string>) => void;
}

export const CarTreeSelect = ({
  items,
  checkedItems,
  onChange,
  sx,
  className,
  ...props
}: CarTreeSelectProps) => {
  const handleChange = (e: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    const withoutCurrent = checkedItems.filter((i) => i !== e.target.name);
    if (e.target.checked) {
      onChange([...withoutCurrent, e.target.name]);
    } else {
      onChange(withoutCurrent);
    }
  };

  const isSomeChildrenChecked = (item: CarTreeItem) => {
    return item.children.length === 0
      ? checkedItems.some((ch) => ch === item.id)
      : item.children.some(isSomeChildrenChecked);
  };

  const renderItem = (item: CarTreeItem, level: number): ReactElement => {
    return (
      <Fragment key={item.id}>
        {item.children.length > 0 ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: 34,
              ml: level * 4,
            }}
          >
            {isSomeChildrenChecked(item) ? (
              <CarIconCheck
                sx={{
                  color: "green",
                  m: 1,
                }}
              />
            ) : (
              <CarIconRemove
                sx={{
                  color: "red",
                  m: 1,
                }}
              />
            )}
            <Typography
              key={item.id}
              variant="par01SemiBold"
              sx={{ color: "black" }}
            >
              {item.label}
            </Typography>
          </Box>
        ) : (
          <CarCheckbox
            key={item.id}
            sx={{ ml: level * 4 }}
            name={item.id}
            checked={checkedItems.some((c) => c === item.id)}
            onChange={handleChange}
            label={item.label}
          />
        )}
        {item.children.map((i) => renderItem(i, level + 1))}
      </Fragment>
    );
  };

  return (
    <FormControl component="div" className={className} sx={sx}>
      <FormGroup {...props}>{items.map((i) => renderItem(i, 0))}</FormGroup>
    </FormControl>
  );
};
